import React, { useState } from "react";
import { connect, useDispatch } from "react-redux";
import { FaFileInvoiceDollar, FaCopy } from "react-icons/fa";

import {
  fetchLifeSettlementCases,
  copyCase,
  showModal,
  editCase,
  handleCaseStatusChanges,
  selectLifeSettlementCase,
  fetchLifeSettlementCase,
  hideModal,
} from "actions";
import {
  activeAgencySelector,
  casesSelector,
  managementChoicesSelector,
  loadingCasesSelector,
  storedCasesFiltersSelector,
  emailTemplateSelector,
} from "reducers";
import IconLink from "components/IconLink";
import Table, { choiceCell, currencyCell, dateCell, dateOverdueCell, idCell } from "components/table";
import { COPY_CASE_MODAL, DELETE_CASE_MODAL } from "components/modals";
import { usStatesChoices } from "../../../options";
import ReactSelect from "react-select";
import * as notifications from "../../../notifications";
import { isAdmin } from "permissions";
import { caseStatusChoices } from "../../../constants";
import { CellViewEditDelete } from "components/icons";
import IconButton from "../../../components/IconButton";

const CaseTable = ({ cases, casesLoading, activeAgency, currentFilters, ...props }) => {
  // Fetch life settlement cases when the active agency changes.
  React.useEffect(() => {
    props.fetchLifeSettlementCases({
      ...currentFilters,
      status: currentFilters.status,
    });
  }, [activeAgency.id]);

  const dispatch = useDispatch();

  let columns = [
    {
      Header: "Action",
      Cell: ({ row }) => {
        const caseId = row.original.id;
        const driveFolderUrl = row.original.drive_pricing_folder_url;
        return (
          <>
            <CellViewEditDelete to={`/cases/${caseId}/policy/`} id={caseId} deleteModal={DELETE_CASE_MODAL} />
            {props.hasAdminPermission && (
              <IconButton
                Icon={FaCopy}
                iconConfig={{ className: "table--action-icon" }}
                tooltip="Copy Case"
                onClick={() => props.showModal(COPY_CASE_MODAL, { id: caseId })}
              />
            )}
            {driveFolderUrl ? (
              <IconLink
                to={driveFolderUrl}
                Icon={FaFileInvoiceDollar}
                iconConfig={{ className: "table--action-icon" }}
                tooltip="View Pricing Folder"
                target="_blank"
                rel="noopener noreferrer"
              />
            ) : null}
          </>
        );
      },
    },
    {
      Header: "Case ID",
      accessor: "id",
      Cell: idCell,
    },
    {
      Header: "Status",
      accessor: "status_name",
      Cell: ({ row }) => {
        const customStyles = {
          control: (provided, state) => ({
            ...provided,
            background: "#fff",
            minHeight: "36px",
            boxShadow: state.isFocused ? null : null,
            width: "200px",
          }),

          menu: (provided, state) => ({
            ...provided,
            width: "240px",
          }),
        };

        let initialValue = {};
        if (row.original.status) {
          initialValue = {
            value: row.original.status,
            label: row.original.status_name,
          };
        }
        let [statusValue, setStatusValue] = useState(initialValue);

        return props.hasAdminPermission ? (
          <ReactSelect
            menuPortalTarget={document.body}
            maxMenuHeight={200}
            name={"status"}
            options={caseStatusChoices}
            onChange={async e => {
              let caseId = row.original.id;
              let newStatus = e.value;
              let updateList = await props.handleCaseStatusChanges(caseId, newStatus, true);
              await props.editCase(caseId, { status: newStatus }, false);
              if (updateList) {
                props.fetchLifeSettlementCases({ ...currentFilters, status: currentFilters.status });
              }
              setStatusValue({ value: e.value, label: e.label });
              notifications.success("Case Status saved successfully");
            }}
            value={statusValue}
            className={`select`}
            menuPlacement="auto"
            styles={customStyles}
            isDisabled={false}
            placeholder="Select Case Status"
          />
        ) : (
          <p style={{ marginTop: "1rem" }}>{row.original.status_name}</p>
        );
      },
    },
    {
      Header: "Insured Names",
      accessor: "insured_names",
      className: "wrap-text",
    },
    {
      Header: "Carrier",
      accessor: "carrier_name",
      className: "wrap-text",
    },
    {
      Header: "Policy #",
      accessor: "policy_number",
    },
    {
      Header: "Face Amount",
      accessor: "face_amount",
      Cell: currencyCell,
    },
    {
      Header: "Product Type",
      accessor: "product_type",
      className: "wrap-text",
    },
    {
      Header: "Jurisdiction",
      accessor: "jurisdiction",
      Cell: choiceCell(usStatesChoices),
      className: "wrap-text",
    },
    {
      Header: "Source",
      accessor: "source_name",
      className: "wrap-text",
    },
    {
      Header: "Sales Rep",
      accessor: "sales_representative_name",
      className: "wrap-text",
    },
    {
      Header: "Create Date",
      accessor: "submission_date",
      Cell: dateCell,
    },
    {
      Header: "Last Edit Date",
      accessor: "last_edit_date",
      Cell: dateCell,
    },
    {
      Header: "Next Due Date",
      accessor: "next_due_date",
      Cell: dateOverdueCell,
    },
  ];

  const noSort = () => null;
  for (let column of columns) {
    column.sortType = noSort;
  }

  return <Table columns={columns} data={cases} emptyMessage={casesLoading ? "Loading Cases data..." : "No data"} />;
};

const mapStateToProps = state => {
  const choices = managementChoicesSelector(state);
  const currentFilters = storedCasesFiltersSelector(state);
  return {
    cases: casesSelector(state),
    casesLoading: loadingCasesSelector(state),
    activeAgency: activeAgencySelector(state),
    hasAdminPermission: isAdmin(state),
    accountChoices: choices.accounts,
    salesRepChoices: choices.case_sales_representatives,
    currentFilters: currentFilters,
    funderEmailTemplates: emailTemplateSelector(state),
  };
};

export default connect(mapStateToProps, {
  fetchLifeSettlementCases,
  showModal,
  editCase,
  copyCase,
  selectLifeSettlementCase,
  handleCaseStatusChanges,
})(CaseTable);
